import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, ArrowPathIcon, PencilIcon, TrashIcon, DocumentDuplicateIcon, PlusIcon } from '@heroicons/react/24/outline';
import platformAdminApi, { AdminPlan, AdminPlanInput, PlanEntitlements } from '@/services/platformAdminApi';

const NUMERIC_ENTITLEMENT_FIELDS: { key: keyof PlanEntitlements; label: string }[] = [
  { key: 'videoProcessingHoursPerMonth', label: 'Video processing hours / month' },
  { key: 'videoTranslationsPerMonth', label: 'Video translations / month' },
  { key: 'audioTranslationsPerMonth', label: 'Audio translations / month' },
  { key: 'speechToTextMinutesPerMonth', label: 'Speech-to-text minutes / month' },
  { key: 'textToSpeechCharactersPerMonth', label: 'Text-to-speech characters / month' },
  { key: 'clipsPerMonth', label: 'Clips / month' },
  { key: 'connectedAccountsLimit', label: 'Connected accounts' },
  { key: 'clientWorkspaces', label: 'Client workspaces' },
  { key: 'maxTeamMembers', label: 'Max team members' },
  { key: 'maxVoices', label: 'Max voices' },
  { key: 'maxAvatars', label: 'Max avatars' },
  { key: 'maxProjects', label: 'Max projects' },
];

const BOOLEAN_ENTITLEMENT_FIELDS: { key: keyof PlanEntitlements; label: string }[] = [
  { key: 'voiceCloning', label: 'Voice cloning' },
  { key: 'lipSync', label: 'Lip sync' },
  { key: 'thumbnailGenerator', label: 'Thumbnail generator' },
  { key: 'schedulingCalendar', label: 'Scheduling calendar' },
  { key: 'analyticsDashboard', label: 'Analytics dashboard' },
  { key: 'approvalWorkflows', label: 'Teams, Assignment & Review workflows' },
  { key: 'brandKits', label: 'Brand kits' },
  { key: 'prioritySupport', label: 'Priority support' },
  { key: 'apiAccess', label: 'API access' },
  { key: 'ssoSaml', label: 'SSO / SAML' },
  { key: 'customAiModels', label: 'Custom AI models' },
];

const BLANK_ENTITLEMENTS: PlanEntitlements = {
  videoProcessingHoursPerMonth: 0,
  videoTranslationsPerMonth: 0,
  audioTranslationsPerMonth: 0,
  speechToTextMinutesPerMonth: 0,
  textToSpeechCharactersPerMonth: 0,
  clipsPerMonth: 0,
  connectedAccountsLimit: 0,
  voiceCloning: false,
  lipSync: false,
  thumbnailGenerator: false,
  schedulingCalendar: false,
  analyticsDashboard: false,
  clientWorkspaces: 0,
  approvalWorkflows: false,
  brandKits: false,
  prioritySupport: false,
  apiAccess: false,
  ssoSaml: false,
  customAiModels: false,
  maxTeamMembers: 0,
  maxVoices: 0,
  maxAvatars: 0,
  maxProjects: 0,
};

const BLANK_PLAN: AdminPlanInput = {
  key: '',
  name: '',
  description: '',
  storageQuotaBytes: 5 * 1024 * 1024 * 1024,
  retentionDaysDefault: 30,
  retentionDaysMax: 30,
  monthlyPriceUsd: 0,
  yearlyPriceUsd: 0,
  isCustomPricing: false,
  isTrial: false,
  trialDays: undefined,
  mostPopular: false,
  offeredOnPricingPage: true,
  entitlements: BLANK_ENTITLEMENTS,
  dodoProductIdMonthly: '',
  dodoProductIdYearly: '',
  isActive: true,
};

const formatBytes = (bytes: number) => `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;

interface PlanFormModalProps {
  mode: 'create' | 'edit';
  initialData: AdminPlanInput;
  onClose: () => void;
  onSaved: () => void;
}

/** One form handles create, edit, and duplicate (duplicate is just "create" with initialData pre-filled from an existing plan, key cleared) — a single component given the ~30-field surface, rather than RolesPage's separate Create/Edit components for its much smaller form. */
const PlanFormModal: React.FC<PlanFormModalProps> = ({ mode, initialData, onClose, onSaved }) => {
  const [form, setForm] = useState<AdminPlanInput>(initialData);
  const [saving, setSaving] = useState(false);

  const setField = <K extends keyof AdminPlanInput>(key: K, value: AdminPlanInput[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };
  const setEntitlement = <K extends keyof PlanEntitlements>(key: K, value: PlanEntitlements[K]) => {
    setForm((prev) => ({ ...prev, entitlements: { ...prev.entitlements, [key]: value } }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.key.trim() || !form.name.trim()) {
      toast.error('Key and name are required');
      return;
    }
    setSaving(true);
    try {
      if (mode === 'create') {
        await platformAdminApi.createPlan(form);
        toast.success('Plan created');
      } else {
        await platformAdminApi.updatePlan(form.key, form);
        toast.success('Plan updated');
      }
      onSaved();
      onClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-[680px] max-h-[85vh] overflow-y-auto shadow-lg rounded-md bg-white dark:bg-gray-800">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          {mode === 'create' ? 'Create Plan' : `Edit Plan: ${form.name}`}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Basic info */}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Key *</label>
                <input
                  type="text"
                  value={form.key}
                  onChange={(e) => setField('key', e.target.value.toLowerCase())}
                  disabled={mode === 'edit'}
                  required
                  placeholder="e.g. creator-pro"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setField('name', e.target.value)}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
              <textarea
                value={form.description || ''}
                onChange={(e) => setField('description', e.target.value)}
                rows={2}
                maxLength={300}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex flex-wrap gap-4">
              {[
                ['isActive', 'Active'],
                ['isTrial', 'Trial plan'],
                ['mostPopular', 'Most popular'],
                ['offeredOnPricingPage', 'Offered on pricing page'],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center text-sm gap-2">
                  <input
                    type="checkbox"
                    checked={!!form[key as keyof AdminPlanInput]}
                    onChange={(e) => setField(key as keyof AdminPlanInput, e.target.checked as any)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  {label}
                </label>
              ))}
            </div>
            {form.isTrial && (
              <div className="w-1/2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Trial days</label>
                <input
                  type="number"
                  value={form.trialDays ?? ''}
                  onChange={(e) => setField('trialDays', e.target.value ? Number(e.target.value) : undefined)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}
          </div>

          {/* Pricing */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Pricing</label>
            <div className="border border-gray-300 rounded-md p-3 space-y-3">
              <label className="flex items-center text-sm gap-2">
                <input
                  type="checkbox"
                  checked={form.isCustomPricing}
                  onChange={(e) => setField('isCustomPricing', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                Custom pricing ("Contact sales" — no self-serve checkout)
              </label>
              {!form.isCustomPricing && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400">Monthly price (USD)</label>
                    <input
                      type="number" min={0} step="0.01"
                      value={form.monthlyPriceUsd ?? ''}
                      onChange={(e) => setField('monthlyPriceUsd', e.target.value ? Number(e.target.value) : undefined)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400">Yearly price (USD)</label>
                    <input
                      type="number" min={0} step="0.01"
                      value={form.yearlyPriceUsd ?? ''}
                      onChange={(e) => setField('yearlyPriceUsd', e.target.value ? Number(e.target.value) : undefined)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              )}
              <details>
                <summary className="text-xs text-gray-400 cursor-pointer">Payment integration (Dodo product IDs)</summary>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400">Dodo monthly product ID</label>
                    <input
                      type="text"
                      value={form.dodoProductIdMonthly || ''}
                      onChange={(e) => setField('dodoProductIdMonthly', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400">Dodo yearly product ID</label>
                    <input
                      type="text"
                      value={form.dodoProductIdYearly || ''}
                      onChange={(e) => setField('dodoProductIdYearly', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </details>
            </div>
          </div>

          {/* Storage & retention */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Storage & retention</label>
            <div className="border border-gray-300 rounded-md p-3 grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400">Storage quota</label>
                <input
                  type="number" min={0}
                  value={form.storageQuotaBytes}
                  onChange={(e) => setField('storageQuotaBytes', Number(e.target.value))}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-400 mt-0.5">{formatBytes(form.storageQuotaBytes)}</p>
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400">Default retention (days)</label>
                <input
                  type="number" min={1}
                  value={form.retentionDaysDefault}
                  onChange={(e) => setField('retentionDaysDefault', Number(e.target.value))}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400">Max retention (days)</label>
                <input
                  type="number" min={1}
                  value={form.retentionDaysMax}
                  onChange={(e) => setField('retentionDaysMax', Number(e.target.value))}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Usage limits */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Usage limits <span className="text-xs text-gray-400">(−1 = unlimited)</span></label>
            <div className="border border-gray-300 rounded-md p-3 max-h-56 overflow-y-auto grid grid-cols-2 gap-3">
              {NUMERIC_ENTITLEMENT_FIELDS.map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-xs text-gray-500 dark:text-gray-400">{label}</label>
                  <input
                    type="number" min={-1}
                    value={form.entitlements[key] as number}
                    onChange={(e) => setEntitlement(key, Number(e.target.value) as any)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              ))}
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400">Translation languages tier</label>
                <select
                  value={form.entitlements.translationLanguagesTier || ''}
                  onChange={(e) => setEntitlement('translationLanguagesTier', (e.target.value || undefined) as any)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">—</option>
                  <option value="50+">50+</option>
                  <option value="100+">100+</option>
                </select>
              </div>
            </div>
          </div>

          {/* Feature flags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Feature flags</label>
            <div className="border border-gray-300 rounded-md p-3 max-h-56 overflow-y-auto grid grid-cols-2 gap-2">
              {BOOLEAN_ENTITLEMENT_FIELDS.map(({ key, label }) => (
                <label key={key} className="flex items-center text-sm gap-2">
                  <input
                    type="checkbox"
                    checked={form.entitlements[key] as boolean}
                    onChange={(e) => setEntitlement(key, e.target.checked as any)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !form.key.trim() || !form.name.trim()}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : mode === 'create' ? 'Create Plan' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const PlanManagementPage: React.FC = () => {
  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<{ mode: 'create' | 'edit'; initialData: AdminPlanInput } | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const res = await platformAdminApi.getPlans();
      setPlans(res.data.data);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPlans(); }, []);

  const toInput = (plan: AdminPlan): AdminPlanInput => {
    const { _id, orgCount, createdAt, updatedAt, ...rest } = plan;
    return rest;
  };

  const handleEdit = (plan: AdminPlan) => setModal({ mode: 'edit', initialData: toInput(plan) });
  const handleDuplicate = (plan: AdminPlan) => setModal({ mode: 'create', initialData: { ...toInput(plan), key: '', name: `${plan.name} (Copy)`, mostPopular: false } });
  const handleCreate = () => setModal({ mode: 'create', initialData: BLANK_PLAN });

  const handleToggleActive = async (plan: AdminPlan) => {
    if (plan.isActive && plan.orgCount > 0) {
      const confirmed = window.confirm(
        `${plan.orgCount} organization(s) are currently on "${plan.name}". Deactivating means any of them will fall back to the most restrictive default limits until reassigned to another plan. Continue?`
      );
      if (!confirmed) return;
    }
    setBusyKey(plan.key);
    try {
      await platformAdminApi.setPlanActive(plan.key, !plan.isActive);
      toast.success(`Plan ${!plan.isActive ? 'activated' : 'deactivated'}`);
      await loadPlans();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to update plan status');
    } finally {
      setBusyKey(null);
    }
  };

  const handleDelete = async (plan: AdminPlan) => {
    if (!window.confirm(`Permanently delete plan "${plan.name}"? This cannot be undone.`)) return;
    setBusyKey(plan.key);
    try {
      await platformAdminApi.deletePlan(plan.key);
      toast.success('Plan deleted');
      await loadPlans();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to delete plan');
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link to="/app/superadmin" className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Plan Management</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadPlans} className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1">
            <ArrowPathIcon className="h-4 w-4" /> Refresh
          </button>
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
          >
            <PlusIcon className="h-4 w-4" /> New Plan
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                {['Key', 'Name', 'Price', 'Orgs', 'Status', 'Flags', ''].map((h) => (
                  <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {plans.map((plan) => (
                <tr key={plan.key} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-5 py-3 font-mono text-xs text-gray-500 dark:text-gray-400">{plan.key}</td>
                  <td className="px-5 py-3 font-medium text-gray-900 dark:text-white">{plan.name}</td>
                  <td className="px-5 py-3 text-gray-500 dark:text-gray-400">
                    {plan.isCustomPricing ? 'Custom' : plan.monthlyPriceUsd ? `$${plan.monthlyPriceUsd}/mo` : 'Free'}
                  </td>
                  <td className="px-5 py-3 text-gray-500 dark:text-gray-400">{plan.orgCount}</td>
                  <td className="px-5 py-3">
                    <button
                      onClick={() => handleToggleActive(plan)}
                      disabled={busyKey === plan.key}
                      className={`text-xs px-2 py-0.5 rounded-full font-medium disabled:opacity-50 ${
                        plan.isActive ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700'
                      }`}
                    >
                      {plan.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-5 py-3 space-x-1">
                    {plan.mostPopular && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Popular</span>}
                    {plan.offeredOnPricingPage && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">On pricing page</span>}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleEdit(plan)} title="Edit" className="text-gray-400 hover:text-blue-600">
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDuplicate(plan)} title="Duplicate" className="text-gray-400 hover:text-blue-600">
                        <DocumentDuplicateIcon className="h-4 w-4" />
                      </button>
                      {plan.orgCount === 0 && (
                        <button onClick={() => handleDelete(plan)} disabled={busyKey === plan.key} title="Delete" className="text-gray-400 hover:text-red-600 disabled:opacity-50">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && plans.length === 0 && (
            <p className="text-center text-gray-400 py-8 text-sm">No plans found</p>
          )}
        </div>
      </div>

      {modal && (
        <PlanFormModal
          mode={modal.mode}
          initialData={modal.initialData}
          onClose={() => setModal(null)}
          onSaved={loadPlans}
        />
      )}
    </div>
  );
};

export default PlanManagementPage;
